import { useState, useRef, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { ChevronDown, FolderOpen, Plus, Check } from 'lucide-react';
import { useProjects, type Project } from '../../hooks/useProjects';

interface ProjectSelectorProps {
  onCreateProject?: () => void;
  isCreateProjectDisabled?: boolean;
}

export function ProjectSelector({ onCreateProject, isCreateProjectDisabled }: ProjectSelectorProps) {
  const { projects, selectedProject, selectProject, isLoadingProjects } = useProjects();
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    }
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleSelectProject = (project: Project) => {
    selectProject(project.id);
    setIsOpen(false);

    // Navigate to the equivalent page for the new project
    const currentPath = location.pathname;
    const projectPathMatch = currentPath.match(/^\/projects\/[^/]+(\/.*)?$/);

    if (projectPathMatch) {
      // If we're on a project-specific page, navigate to the same subpage for the new project
      const subPath = projectPathMatch[1] || '';
      navigate(`/projects/${project.id}${subPath}`);
    } else {
      // If we're not on a project page, navigate to the project overview
      navigate(`/projects/${project.id}`);
    }
  };

  if (isLoadingProjects) {
    return <div className="h-10 bg-muted/50 rounded-lg animate-pulse" />;
  }

  return (
    <div className="relative" ref={dropdownRef}>
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="flex items-center gap-2.5 px-3 py-2 w-full text-left bg-muted/30 border border-border rounded-lg hover:bg-muted/50 transition-colors"
      >
        <FolderOpen className="h-4 w-4 text-muted-foreground flex-shrink-0" />
        <span className="flex-1 truncate text-[13px] font-medium">
          {selectedProject ? selectedProject.name : 'Select project'}
        </span>
        <ChevronDown className={`h-3.5 w-3.5 text-muted-foreground transition-transform ${isOpen ? 'rotate-180' : ''}`} />
      </button>

      {isOpen && (
        <div className="absolute top-full left-0 right-0 mt-1 bg-card border border-border rounded-lg shadow-lg z-50 overflow-hidden">
          <div className="max-h-48 overflow-y-auto">
            {projects.length === 0 ? (
              <div className="px-3 py-3 text-center text-xs text-muted-foreground">No projects</div>
            ) : (
              <div className="py-1">
                {projects.map((project) => (
                  <button
                    key={project.id}
                    onClick={() => handleSelectProject(project)}
                    className="flex items-center gap-2.5 w-full px-3 py-2 text-left hover:bg-muted/50 transition-colors"
                  >
                    <FolderOpen className="h-4 w-4 text-muted-foreground" />
                    <span className="flex-1 truncate text-[13px]">{project.name}</span>
                    {selectedProject?.id === project.id && <Check className="h-4 w-4 text-primary" />}
                  </button>
                ))}
              </div>
            )}
          </div>

          {onCreateProject && (
            <>
              <div className="border-t border-border" />
              <button
                onClick={() => { if (!isCreateProjectDisabled) { setIsOpen(false); onCreateProject(); } }}
                disabled={isCreateProjectDisabled}
                className={`flex items-center gap-2.5 w-full px-3 py-2 text-left text-[13px] transition-colors ${
                  isCreateProjectDisabled
                    ? 'text-muted-foreground opacity-50 cursor-not-allowed'
                    : 'text-primary hover:bg-muted/50'
                }`}
              >
                <Plus className="h-4 w-4" />
                <span>New project</span>
                {isCreateProjectDisabled && <span className="ml-auto text-[10px] text-destructive">Limit</span>}
              </button>
            </>
          )}
        </div>
      )}
    </div>
  );
}
